/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from 'react';
import { unlink, User, UserInfo } from 'firebase/auth';
import trashcanImg from 'dndimages/delete-icon.png';
import iconGoogle from './icon-google.svg';
import iconFacebook from './icon-facebook.svg';
import iconEmail from './icon-email.svg';
import { ReactSVG } from 'react-svg';
import AuthService from 'services/AuthService';

interface ManageProvidersProps {
    user: User;
    onUpdate: () => void;
    onAddEmailLink: () => void;
    onRemoveEmailLink: () => void;
}
const ManageProviders: React.FC<ManageProvidersProps> = ({ user, onUpdate, onAddEmailLink, onRemoveEmailLink }) => {
    const [providers, setProviders] = useState<UserInfo[]>([]);

    useEffect(() => {
        setProviders(user.providerData);
    }, [user, user?.providerData, user?.providerData.length])

    const handleLink = async (providerId: string) => {
        if (providerId === 'password') {
            onAddEmailLink();
        } else if (providerId.toLowerCase() === 'google.com') {
            await AuthService.linkWithGoogle(onUpdate);
        } else if (providerId.toLowerCase() === 'facebook.com') {
            await AuthService.linkWithFacebook(onUpdate);
        }
    }

    const handleUnlink = async (providerId: string) => {
        if (!user) {
            return;
        }
        try {
            await unlink(user, providerId);
            setProviders((prevProviders) => prevProviders.filter((p) => p.providerId !== providerId));
            alert(`Successfully unlinked ${getProviderName(providerId)}`);
            onRemoveEmailLink();
        } catch (error) {
            console.error('Error unlinking provider:', error);
            alert('Failed to unlink provider.');
        }
    };

    const getProvider = (provider: string) => {
        if (provider.toLowerCase() === 'password') {
            return <ReactSVG src={iconEmail} title='Email account' style={{ height: '16px', width: '16px' }} />;
        }
        if (provider.toLowerCase() === 'google.com') {
            return <ReactSVG src={iconGoogle} title='Google account' style={{ height: '16px', width: '16px' }} />;
        }
        if (provider.toLowerCase() === 'facebook.com') {
            return <ReactSVG src={iconFacebook} title='Facebook account' style={{ height: '16px', width: '16px' }} />;
        }
        return provider;
    }
    const getProviderName = (provider: string) => {
        if (provider.toLowerCase() === 'password') {
            return 'email';
        }
        if (provider.toLowerCase() === 'google.com') {
            return 'Google account';
        }
        if (provider.toLowerCase() === 'facebook.com') {
            return 'Facebook account';
        }
        return provider;
    }
    const allowedRegistrars = ['password', 'google.com', 'facebook.com'];
    const availableRegs = [];
    for (const allowedRegistrar of allowedRegistrars) {
        let exists = false;
        for (const provider of providers) {
            if (provider.providerId.toLowerCase() === allowedRegistrar) {
                exists = true;
                break;
            }
        }
        if (!exists) {
            availableRegs.push(allowedRegistrar);
        }
    }
    const canUlink = providers.length > 1;

    const tdStyle = { border: '1px solid grey' };
    return (
            <div style={{textAlign: 'center'}}>
                <br/>
                <table style={{ margin: 'auto' ,border: '3px solid black', borderCollapse: 'collapse', textAlign: 'center' }}>
                    <thead>
                        <tr>
                            <th style={tdStyle}></th>
                            <th style={tdStyle}>Email</th>
                            <th style={tdStyle}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {providers.length === 0 ? (
                            <tr><td>No providers linked to this account.</td></tr>
                        ) : (
                            providers.map((provider) => (
                                <tr key={provider.providerId}>
                                    <td style={tdStyle}>{getProvider(provider.providerId)}</td>
                                    <td style={tdStyle}>{provider.email || 'N/A'}</td>
                                    <td style={tdStyle}>
                                        {canUlink && (
                                        <a href="#" onClick={() => handleUnlink(provider.providerId)} title='Remove this account linkage'>
                                            <img style={{ height: '16px' }} src={trashcanImg} alt="Delete" />
                                        </a>)}
                                    </td>
                                </tr>
                            )))}
                            <tr>
                                <td style={tdStyle} colSpan={3}>
                                    {(availableRegs.map((provider) => (
                                        <button key={provider} onClick={() => handleLink(provider)}
                                                title={`Link ${getProviderName(provider)} to account`} >
                                            {getProvider(provider)}
                                            <span style={{fontSize: '12px'}}>Link</span>
                                        </button>
                                    )))}
                                </td>
                            </tr>
                    </tbody>
                </table>
            </div>
    );
};

export default ManageProviders;
